/**
 * ColumnVisibilityDropdown - Thin wrapper over shared ColumnVisibilityDropdown
 * for the deck list view. Connects to deck-specific settings atom.
 */
import type { ReactElement } from 'react';
import { useCallback } from 'react';

import { useAtom } from 'jotai';

import SharedColumnVisibilityDropdown from '@/components/ColumnVisibilityDropdown/ColumnVisibilityDropdown';
import {
  DECK_COLUMN_GROUPS,
  DECK_COLUMN_LABELS,
  DEFAULT_COLUMN_VISIBILITY,
  deckEditorSettingsAtom,
  type DeckColumnKey,
} from '@/features/decks/state/deckEditorSettings';

// ============================================================================
// Inline column hint
// ============================================================================

const INLINE_HINT_COLUMNS: DeckColumnKey[] = ['keywords'];

// ============================================================================
// Types
// ============================================================================

export interface ColumnVisibilityDropdownProps {
  className?: string;
}

// ============================================================================
// Main Component
// ============================================================================

export default function ColumnVisibilityDropdown({ className }: ColumnVisibilityDropdownProps): ReactElement {
  const [settings, setSettings] = useAtom(deckEditorSettingsAtom);

  const toggleColumn = useCallback(
    (column: DeckColumnKey) => {
      setSettings((prev) => ({
        ...prev,
        columnVisibility: {
          ...prev.columnVisibility,
          [column]: !prev.columnVisibility[column],
        },
      }));
    },
    [setSettings],
  );

  const resetToDefaults = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      columnVisibility: DEFAULT_COLUMN_VISIBILITY,
    }));
  }, [setSettings]);

  const activeCount = Object.values(settings.columnVisibility).filter(Boolean).length;

  return (
    <SharedColumnVisibilityDropdown<DeckColumnKey>
      visibility={settings.columnVisibility}
      onToggle={toggleColumn}
      onReset={resetToDefaults}
      groups={DECK_COLUMN_GROUPS}
      labels={DECK_COLUMN_LABELS}
      inlineColumns={INLINE_HINT_COLUMNS}
      activeCount={activeCount}
      className={className}
    />
  );
}
